// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  Badge,
  Divider,
  SimpleGrid,
  useToast,
  Spinner,
  Center,
  IconButton,
} from '@chakra-ui/react';
import { ArrowBackIcon, EditIcon, RepeatIcon, CloseIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUrl';

interface Contract {
  id: string;
  leadId?: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerCompany?: string;
  customerPhone?: string;
  
  plan?: string;
  doorCount: number;
  value: number;
  paymentFrequency: 'monthly' | 'annual';
  
  startDate: string | Date;
  endDate: string | Date;
  signedDate?: string | Date;
  
  status: 'active' | 'expiring_soon' | 'expired' | 'cancelled' | string;
  
  autoRenew: boolean;
  renewalStatus?: string;
  renewalNotes?: string;
  renewalNoticeDays?: number;
  
  contractUrl?: string;
  signedContractUrl?: string;
  
  cancellationReason?: string;
  cancellationDate?: string;
  
  createdAt: string | Date;
  updatedAt: string | Date;
  notes?: string;
  
  // Legacy fields
  planTier?: 'starter' | 'professional' | 'enterprise';
  monthlyValue?: number;
  annualValue?: number;
  contractTerm?: number;
  features?: string[];
  supportLevel?: string;
  createdByName?: string;
}

const ContractDetailPage: React.FC = () => {
  const { t } = useTranslation('contracts');
  const navigate = useNavigate();
  const toast = useToast();
  const { contractId } = useParams<{ contractId: string }>();
  const { user } = useAuth();

  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);

  // Debug: Log that translations are loaded
  useEffect(() => {
    console.log('[ContractDetailPage] Translations loaded:', {
      'contracts.detail.contractInfo': t('detail.contractInfo'),
      'contracts.detail.contractId': t('detail.contractId'),
    });
  }, [t]);

  // Helper function to convert Firestore timestamp to Date
  const toDate = (timestamp: any): Date | null => {
    if (!timestamp) return null;
    
    // If it's a Firestore Timestamp object with toDate() method
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // If it's an object with seconds property (Firestore Timestamp)
    if (timestamp.seconds !== undefined) {
      return new Date(timestamp.seconds * 1000);
    }
    
    // If it's already a Date
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // If it's a string, parse it
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
  };

  // Helper function to format date for display
  const formatDate = (timestamp: any): string => {
    const date = toDate(timestamp);
    if (!date) return 'N/A';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  useEffect(() => {
    if (contractId) {
      fetchContract();
    }
  }, [contractId]);

  const fetchContract = async () => {
    if (!contractId || !user) return;
    
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/getContract?contractId=${contractId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch contract');

      const data = await response.json();
      setContract(data.contract);
    } catch (error) {
      console.error('Error fetching contract:', error);
      toast({
        title: t('errors.fetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = () => {
    navigate(`/admin/contracts/${contractId}/renew`);
  };

  const handleCancel = async () => {
    if (!confirm(t('confirmCancel'))) return;
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/cancelContract`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ contractId }),
        }
      );

      if (!response.ok) throw new Error('Failed to cancel contract');

      toast({
        title: t('success.cancelled'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Redirect to dashboard after successful cancellation
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1500);
    } catch (error) {
      toast({
        title: t('errors.cancelFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'expiring_soon':
        return 'orange';
      case 'expired':
        return 'red';
      case 'cancelled':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'starter':
        return 'blue';
      case 'professional':
        return 'purple';
      case 'enterprise':
        return 'pink';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!contract) {
    return (
      <Box>
        <Text>{t('notFound')}</Text>
        <Button onClick={() => navigate('/admin/dashboard')} mt={4}>
          Back to Contracts
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <IconButton
            icon={<ArrowBackIcon />}
            aria-label="Back"
            onClick={() => navigate('/admin/dashboard')}
          />
          <Box>
            <Heading size="lg">{contract.customerName}</Heading>
            <Text color="gray.600">{contract.companyName}</Text>
          </Box>
        </HStack>
        <HStack spacing={2}>
          <Badge colorScheme={getStatusColor(contract.status)} fontSize="md" p={2}>
            {contract.status}
          </Badge>
          {contract.planTier && (
            <Badge colorScheme={getTierColor(contract.planTier)} fontSize="md" p={2}>
              {contract.planTier}
            </Badge>
          )}
        </HStack>
      </Flex>

      {/* Actions */}
      <HStack spacing={2} mb={6}>
        <Button
          leftIcon={<EditIcon />}
          onClick={() => navigate(`/admin/contracts/${contractId}/edit`)}
        >
          {t('actions.edit')}
        </Button>
        {(contract.status === 'active' || contract.status === 'expiring_soon') && (
          <Button leftIcon={<RepeatIcon />} colorScheme="blue" onClick={handleRenew}>
            {t('actions.renew')}
          </Button>
        )}
        {contract.status === 'active' && (
          <Button
            leftIcon={<CloseIcon />}
            colorScheme="red"
            variant="outline"
            onClick={handleCancel}
          >
            {t('actions.cancel')}
          </Button>
        )}
      </HStack>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Contract Information */}
        <Card>
          <CardHeader>
            <Heading size="md">{t('detail.contractInfo')}</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.contractId')}
                </Text>
                <Text>{contract.id}</Text>
              </Box>
              <Divider />
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.planTier')}
                </Text>
                {contract.planTier ? (
                  <Badge colorScheme={getTierColor(contract.planTier)}>
                    {contract.planTier}
                  </Badge>
                ) : (
                  <Text>{contract.plan || 'N/A'}</Text>
                )}
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.doorCount')}
                </Text>
                <Text>{contract.doorCount} doors</Text>
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.contractTerm')}
                </Text>
                <Text>{contract.contractTerm ? `${contract.contractTerm} months` : 'N/A'}</Text>
              </Box>
              <Divider />
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.startDate')}
                </Text>
                <Text>{formatDate(contract.startDate)}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.endDate')}
                </Text>
                <Text>{formatDate(contract.endDate)}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.autoRenew')}
                </Text>
                <Badge colorScheme={contract.autoRenew ? 'green' : 'gray'}>
                  {contract.autoRenew ? 'Enabled' : 'Disabled'}
                </Badge>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        {/* Customer Information */}
        <Card>
          <CardHeader>
            <Heading size="md">{t('detail.customerInfo')}</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.customerName')}
                </Text>
                <Text>{contract.customerName}</Text>
              </Box>
              {contract.companyName && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600">
                    {t('detail.companyName')}
                  </Text>
                  <Text>{contract.companyName}</Text>
                </Box>
              )}
              <Divider />
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.email')}
                </Text>
                <Text>{contract.customerEmail}</Text>
              </Box>
              {contract.customerPhone && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600">
                    {t('detail.phone')}
                  </Text>
                  <Text>{contract.customerPhone}</Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Payment Information */}
        <Card>
          <CardHeader>
            <Heading size="md">{t('detail.paymentInfo')}</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.monthlyValue')}
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  ${((contract.monthlyValue || contract.value / (contract.paymentFrequency === 'annual' ? 12 : 1)) as number).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </Text>
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.annualValue')}
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                  ${(contract.annualValue || contract.value * (contract.paymentFrequency === 'annual' ? 1 : 12) as number).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </Text>
              </Box>
              <Divider />
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">
                  {t('detail.paymentFrequency')}
                </Text>
                <Badge>
                  {contract.paymentFrequency === 'monthly' ? 'Monthly' : 'Annual'}
                </Badge>
              </Box>
              {contract.paymentMethod && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600">
                    {t('detail.paymentMethod')}
                  </Text>
                  <Text>{contract.paymentMethod}</Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Contract Terms */}
        <Card>
          <CardHeader>
            <Heading size="md">{t('detail.terms')}</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              {contract.features && contract.features.length > 0 && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={2}>
                    {t('detail.includedFeatures')}
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {contract.features.map((feature, index) => (
                      <Text key={index} fontSize="sm">
                        • {feature}
                      </Text>
                    ))}
                  </VStack>
                </Box>
              )}
              {contract.supportLevel && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600">
                    {t('detail.supportLevel')}
                  </Text>
                  <Text>{contract.supportLevel}</Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Renewal/Cancellation Info */}
      {(contract.renewalNotes || contract.cancellationReason) && (
        <Card mt={6}>
          <CardHeader>
            <Heading size="md">
              {contract.renewalNotes
                ? t('detail.renewalInfo')
                : t('detail.cancellationInfo')}
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              {contract.renewalStatus && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600">
                    {t('detail.renewalStatus')}
                  </Text>
                  <Badge>{contract.renewalStatus}</Badge>
                </Box>
              )}
              {contract.renewalNotes && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600">
                    {t('detail.notes')}
                  </Text>
                  <Text>{contract.renewalNotes}</Text>
                </Box>
              )}
              {contract.cancellationReason && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600">
                    {t('detail.cancellationReason')}
                  </Text>
                  <Text>{contract.cancellationReason}</Text>
                </Box>
              )}
              {contract.cancellationDate && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600">
                    {t('detail.cancellationDate')}
                  </Text>
                  <Text>
                    {new Date(contract.cancellationDate).toLocaleDateString()}
                  </Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      )}
    </Box>
  );
};

export default ContractDetailPage;
